import type {
  PostingExprNode,
  PostingNode,
  PostingTextKind,
} from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  type FreeTextFilter,
  isMultiFilter,
} from '../../../filters/types/filters';
import { buildOrNode, buildTxtNode, wrapExprNode } from './node-builders';
import { PostingDiscoveryTags } from './tags';

const buildTextNodesFromStrings = (
  texts: string[],
  kind: PostingTextKind
): PostingExprNode | null => {
  const trimmedTexts = texts
    .map((t) => String(t).trim())
    .filter((t) => t.length > 0);

  if (trimmedTexts.length === 0) return null;

  const textNodes = trimmedTexts.map((text) => buildTxtNode(kind, text));
  if (textNodes.length === 1) {
    return textNodes[0] ?? null;
  }
  return buildOrNode(textNodes);
};

export const buildKeywordsFilter = (
  filters: Filter[]
): PostingNode | PostingNode[] | null => {
  const multiKeywords = findFilterById(filters, FilterId.MULTI_KEYWORDS);

  if (multiKeywords && isMultiFilter(multiKeywords)) {
    const instanceNodes = multiKeywords.instances
      .filter(
        (inst): inst is FreeTextFilter => inst?.type === FilterType.FREE_TEXT
      )
      .map((instance) => {
        const exprNode = buildTextNodesFromStrings(
          instance.text || [],
          PostingDiscoveryTags.Description
        );
        if (!exprNode) return null;
        return wrapExprNode(exprNode, instance.isNot);
      })
      .filter((node): node is PostingNode => node !== null);

    if (instanceNodes.length === 0) return null;
    if (instanceNodes.length === 1) {
      const [singleNode] = instanceNodes;
      return singleNode ?? null;
    }
    return instanceNodes;
  }

  const keywordsFilter = findFilterById(filters, FilterId.KEYWORDS);
  if (!keywordsFilter || keywordsFilter.type !== FilterType.FREE_TEXT) {
    return null;
  }

  const exprNode = buildTextNodesFromStrings(
    keywordsFilter.text || [],
    PostingDiscoveryTags.Description
  );
  if (!exprNode) return null;

  return wrapExprNode(exprNode, keywordsFilter.isNot);
};

export const buildTitlesFilter = (
  filters: Filter[]
): PostingNode | PostingNode[] | null => {
  const multiTitle = findFilterById(filters, FilterId.MULTI_TITLE);

  if (multiTitle && isMultiFilter(multiTitle)) {
    const instanceNodes = multiTitle.instances
      .filter(
        (inst): inst is FreeTextFilter => inst?.type === FilterType.FREE_TEXT
      )
      .map((instance) => {
        const exprNode = buildTextNodesFromStrings(
          instance.text || [],
          PostingDiscoveryTags.Jobtitle
        );
        if (!exprNode) return null;
        return wrapExprNode(exprNode, instance.isNot);
      })
      .filter((node): node is PostingNode => node !== null);

    if (instanceNodes.length === 0) return null;
    if (instanceNodes.length === 1) {
      const [singleNode] = instanceNodes;
      return singleNode ?? null;
    }
    return instanceNodes;
  }

  const singleTitleFilter = findFilterById(filters, FilterId.TITLE);
  if (!singleTitleFilter || singleTitleFilter.type !== FilterType.FREE_TEXT) {
    return null;
  }

  const exprNode = buildTextNodesFromStrings(
    singleTitleFilter.text || [],
    PostingDiscoveryTags.Jobtitle
  );
  if (!exprNode) return null;

  return wrapExprNode(exprNode, singleTitleFilter.isNot);
};
